import { useEffect } from 'react';
import axios from 'axios';

export const useChatPolling = ({
  user,
  BACKEND_URL,
  selectedBookingChat,
  chatTargets,
  chatLastSeenRef,
  chatOpenedRef,
  chatUnreadRef,
  setSelectedBookingChat,
  setRecentCancelledBookings,
  showNotification
}) => {
  useEffect(() => {
    if (!user || !BACKEND_URL) return;
    if (selectedBookingChat) return;
    if (!chatTargets || chatTargets.length === 0) return;

    let cancelled = false;

    const checkChats = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      for (const target of chatTargets) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/chat/${target.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const messages = response.data.messages || [];
          if (!messages.length) continue;

          const lastMessage = messages[messages.length - 1];
          const lastKey = lastMessage.id || lastMessage.timestamp;
          const targetKey = `${target.type}:${target.id}`;
          const lastSeen = chatLastSeenRef.current[targetKey];

          const hasOpenedBefore = Boolean(chatOpenedRef.current[targetKey]);

          if (user?.user_type === 'client' && lastMessage?.type === 'system') {
            const text = String(lastMessage.message || '').toLowerCase();
            if (text.includes('reserva cancelada')) {
              const barbershopId = lastMessage.barbershop_id || target.barbershop_id || target.barbershop?.id;
              const bookingId = lastMessage.booking_id || target.id;
              if (barbershopId && bookingId) {
                setRecentCancelledBookings((prev) => ({
                  ...prev,
                  [bookingId]: { barbershopId, timestamp: Date.now() }
                }));
              }
              if (!cancelled) {
                showNotification?.('Reserva cancelada', 'El barbero canceló tu reserva', 'info');
              }
            }
          }

          if (!lastSeen) {
            if (lastMessage.sender_id && lastMessage.sender_id !== user.id) {
              chatLastSeenRef.current[targetKey] = lastKey;
              if (!cancelled) {
                if (!hasOpenedBefore) {
                  chatOpenedRef.current[targetKey] = true;
                  setSelectedBookingChat?.(target);
                } else {
                  chatUnreadRef.current[targetKey] = (chatUnreadRef.current[targetKey] || 0) + 1;
                  showNotification?.('Nuevo mensaje', `Tienes un mensaje de ${target.barber?.name || target.client_name || 'Cliente'}`, 'info');
                }
              }
              break;
            }
            chatLastSeenRef.current[targetKey] = lastKey;
            continue;
          }

          if (lastKey !== lastSeen) {
            chatLastSeenRef.current[targetKey] = lastKey;
            if (lastMessage.sender_id && lastMessage.sender_id !== user.id) {
              if (!cancelled) {
                if (!hasOpenedBefore) {
                  chatOpenedRef.current[targetKey] = true;
                  setSelectedBookingChat?.(target);
                } else {
                  chatUnreadRef.current[targetKey] = (chatUnreadRef.current[targetKey] || 0) + 1;
                  showNotification?.('Nuevo mensaje', `Tienes un mensaje de ${target.barber?.name || target.client_name || 'Cliente'}`, 'info');
                }
              }
              break;
            }
          }
        } catch (error) {
          // ignore per-target errors
        }
      }
    };

    checkChats();
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      checkChats();
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    user,
    BACKEND_URL,
    selectedBookingChat,
    chatTargets,
    chatLastSeenRef,
    chatOpenedRef,
    chatUnreadRef,
    setSelectedBookingChat,
    setRecentCancelledBookings,
    showNotification
  ]);

  useEffect(() => {
    if (!selectedBookingChat || !user || !BACKEND_URL) return;

    const syncLastSeen = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await axios.get(`${BACKEND_URL}/api/chat/${selectedBookingChat.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const messages = response.data.messages || [];
        if (messages.length) {
          const lastMessage = messages[messages.length - 1];
          chatLastSeenRef.current[`${selectedBookingChat.type || 'booking'}:${selectedBookingChat.id}`] =
            lastMessage.id || lastMessage.timestamp;
          chatOpenedRef.current[`${selectedBookingChat.type || 'booking'}:${selectedBookingChat.id}`] = true;
          chatUnreadRef.current[`${selectedBookingChat.type || 'booking'}:${selectedBookingChat.id}`] = 0;
        }
      } catch (error) {
        // ignore
      }
    };

    syncLastSeen();
  }, [selectedBookingChat, user, BACKEND_URL, chatLastSeenRef, chatOpenedRef, chatUnreadRef]);
};
